// Deep Signal belief page: where one belief surfaces across the client's
// series. Every number comes from the server (app/services/belief_view.py);
// this component only lays it out. Clients see percentages; admins also see
// the counts behind them on hover, set which questions ask for the belief, and
// choose whether the latest read's report includes this block.

import { Fragment, useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { ArrowLeft, Download, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import {
  getBeliefView, downloadBeliefExport, saveBeliefQuestionFlags,
  getReportBelief, saveReportBelief,
} from '@/api/belief-view';
import type { BeliefCell, BeliefQuestion, BeliefView } from '@/api/belief-view';
import { SectionLabel, SectionTitle, DSLoadingRow, DSEmptyState } from '../components/section-primitives';

const MUTED = 'var(--muted-foreground)';
const TEAL = 'var(--alpha-teal)';

function pct(cell: BeliefCell | undefined, field: 'share' | 'primary_when_present' | 'primary_when_absent' = 'share'): string {
  if (!cell) return '—';
  if (cell.status === 'not_in_battery') return 'not asked';
  if (cell.status === 'belief_not_measured') return 'n/m';
  const value = cell[field];
  if (value == null) return '—';
  return `${Math.round(value * 100)}%`;
}

function isDirectional(cell: BeliefCell | undefined, field: 'share' | 'primary_when_present' | 'primary_when_absent'): boolean {
  if (!cell || cell.status !== 'measured') return false;
  if (field === 'share') return Boolean(cell.directional);
  if (field === 'primary_when_present') return Boolean(cell.primary_directional);
  return Boolean(cell.primary_absent_directional);
}

/** Admin hover text: the counts behind a percentage. Undefined for a client,
 *  whose payload carries no counts. */
function countsTitle(cell: BeliefCell | undefined, field: 'share' | 'primary_when_present' | 'primary_when_absent'): string | undefined {
  if (!cell || cell.answers == null) return undefined;
  if (field === 'share') return `${cell.present} of ${cell.answers} answers`;
  if (field === 'primary_when_present') return `${cell.primary_present} of ${cell.present} answers where present`;
  return `${cell.primary_absent} of ${cell.absent} answers where absent`;
}

function Pct({ cell, field = 'share', strong = false }: {
  cell: BeliefCell | undefined;
  field?: 'share' | 'primary_when_present' | 'primary_when_absent';
  strong?: boolean;
}) {
  const directional = isDirectional(cell, field);
  return (
    <span title={countsTitle(cell, field)} style={{ fontWeight: strong ? 600 : 400, fontVariantNumeric: 'tabular-nums' }}>
      {pct(cell, field)}
      {directional && <span style={{ color: MUTED }} title="Directional: fewer than 20 answers">*</span>}
    </span>
  );
}

/** One small bar per read. Not called a trend: cells under 20 answers are
 *  dashed and starred as directional. */
function ReadBars({ cells, labels }: { cells: BeliefCell[]; labels: string[] }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height: '28px' }}>
      {cells.map((c, i) => {
        const measured = c.status === 'measured' && c.share != null;
        const h = measured ? Math.max(2, Math.round((c.share ?? 0) * 26)) : 2;
        const title = `${labels[i]}: ${pct(c)}${c.directional ? ' (directional)' : ''}${c.answers != null ? ` · ${c.present}/${c.answers}` : ''}`;
        return (
          <div
            key={c.run_id}
            title={title}
            style={{
              width: '9px',
              height: `${h}px`,
              borderRadius: '2px',
              backgroundColor: measured ? TEAL : 'transparent',
              opacity: measured ? (c.directional ? 0.45 : 0.55 + 0.45 * (i + 1) / cells.length) : 1,
              border: measured ? (c.directional ? '1px dashed var(--foreground)' : 'none') : '1px dotted var(--border)',
            }}
          />
        );
      })}
    </div>
  );
}

function Tile({ label, cell, field, series, readLabels }: {
  label: string;
  cell: BeliefCell;
  field: 'share' | 'primary_when_present' | 'primary_when_absent';
  series: BeliefCell[];
  readLabels: string[];
}) {
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: '10px', padding: '16px 18px', backgroundColor: 'var(--background)' }}>
      <p style={{ fontSize: 'var(--text-xs)', color: MUTED, fontWeight: 600, marginBottom: '6px' }}>{label}</p>
      <p style={{ fontSize: 'var(--text-3xl)', color: 'var(--foreground)', marginBottom: '6px' }}>
        <Pct cell={cell} field={field} strong />
      </p>
      <p style={{ fontSize: 'var(--text-xs)', color: MUTED }}>
        {series.map((c, i) => (
          <Fragment key={c.run_id}>
            {i > 0 && ' → '}
            <span title={readLabels[i]}><Pct cell={c} field={field} /></span>
          </Fragment>
        ))}
      </p>
    </div>
  );
}

function QuestionRow({ q, readLabels, isAdmin, flagged, onToggleFlag, saving }: {
  q: BeliefQuestion;
  readLabels: string[];
  isAdmin: boolean;
  flagged: boolean;
  onToggleFlag: (queryId: string, on: boolean) => void;
  saving: boolean;
}) {
  const [open, setOpen] = useState(false);
  const quote = q.top_quote;
  return (
    <>
      <tr style={{ borderTop: '1px solid var(--border)', verticalAlign: 'top' }}>
        <td style={{ padding: '10px 8px 10px 0', maxWidth: '380px' }}>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--foreground)', lineHeight: 1.4 }}>{q.text}</p>
          <p style={{ fontSize: 'var(--text-xs)', color: MUTED, marginTop: '3px' }}>
            {[q.persona, q.question_type].filter(Boolean).join(' · ')}
            {q.marker && (
              <span
                title={q.marker.applied_at ? `Series rebaselined ${q.marker.applied_at.slice(0, 10)}` : undefined}
                style={{ marginLeft: '8px', padding: '1px 6px', borderRadius: '99px', border: '1px solid var(--border)', color: 'var(--foreground)' }}
              >
                {q.marker.position === 'after' ? 'New wording' : 'Old wording'} · edited at rebaseline
              </span>
            )}
          </p>
        </td>
        <td style={{ padding: '10px 8px', fontSize: 'var(--text-xs)', color: MUTED, whiteSpace: 'nowrap' }}>
          {isAdmin && q.asks_source === 'admin_flag' ? (
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: saving ? 'wait' : 'pointer' }}
              title="Admin: mark this question as written to surface the belief">
              <input type="checkbox" checked={flagged} disabled={saving}
                onChange={e => onToggleFlag(q.query_id, e.target.checked)} />
              {flagged ? 'Yes' : 'No'}
            </label>
          ) : (
            <span title={q.asks_source === 'framework' ? 'From the framework question mapping' : undefined}>
              {q.asks_for_belief ? 'Yes' : 'No'}
            </span>
          )}
        </td>
        <td style={{ padding: '10px 8px' }}><ReadBars cells={q.series} labels={readLabels} /></td>
        <td style={{ padding: '10px 8px', fontSize: 'var(--text-sm)', textAlign: 'right' }}><Pct cell={q.latest} strong /></td>
        <td style={{ padding: '10px 8px', fontSize: 'var(--text-sm)', textAlign: 'right' }}><Pct cell={q.latest} field="primary_when_present" /></td>
        <td style={{ padding: '10px 0 10px 8px', maxWidth: '340px' }}>
          {quote ? (
            <button
              onClick={() => setOpen(o => !o)}
              style={{ textAlign: 'left', background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--foreground)' }}
              title={q.quotes.length > 1 ? `See all ${q.quotes.length} quotes` : 'See the quote'}
            >
              <span style={{ fontSize: 'var(--text-xs)', fontStyle: 'italic', lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: open ? 'unset' : 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                “{quote.quote}”
              </span>
              <span style={{ fontSize: 'var(--text-xs)', color: MUTED, display: 'flex', alignItems: 'center', gap: '4px', marginTop: '3px' }}>
                {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                {quote.model_label}{q.quotes.length > 1 ? ` · ${q.quotes.length} quotes` : ''}
              </span>
            </button>
          ) : (
            <span style={{ fontSize: 'var(--text-xs)', color: MUTED }}>—</span>
          )}
        </td>
      </tr>
      {open && q.quotes.length > 0 && (
        <tr>
          <td colSpan={6} style={{ padding: '0 0 12px 0' }}>
            <div style={{ border: '1px solid var(--border)', borderRadius: '8px', padding: '10px 14px', backgroundColor: 'var(--muted)' }}>
              <p style={{ fontSize: 'var(--text-xs)', color: MUTED, fontWeight: 600, marginBottom: '6px' }}>
                Every judge quote for this question, {readLabels[readLabels.length - 1]}
              </p>
              {q.quotes.map((qq, i) => (
                <p key={i} style={{ fontSize: 'var(--text-xs)', lineHeight: 1.55, marginBottom: '6px', color: 'var(--foreground)' }}>
                  <span style={{ fontWeight: 600 }}>{qq.model_label}</span>
                  <span style={{ color: MUTED }}>{qq.score != null ? ` · score ${qq.score}` : ''}{qq.primary_rec ? ' · primary rec' : ''}</span>
                  {' '}<span style={{ fontStyle: 'italic' }}>“{qq.quote}”</span>
                </p>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function SmallTable({ title, column, rows, readLabels }: {
  title: string;
  column: string;
  rows: { key: string; label: string; note?: string; latest: BeliefCell; series: BeliefCell[] }[];
  readLabels: string[];
}) {
  return (
    <div style={{ flex: '1 1 340px', minWidth: 0 }}>
      <p style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--foreground)', marginBottom: '8px' }}>{title}</p>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-sm)' }}>
        <thead>
          <tr style={{ color: MUTED, fontSize: 'var(--text-xs)', textAlign: 'left' }}>
            <th style={{ padding: '6px 8px 6px 0', fontWeight: 600 }}>{column}</th>
            <th style={{ padding: '6px 8px', fontWeight: 600, textAlign: 'right' }}>Present</th>
            <th style={{ padding: '6px 8px', fontWeight: 600, textAlign: 'right' }}>Primary when present</th>
            <th style={{ padding: '6px 0 6px 8px', fontWeight: 600 }}>By read</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.key} style={{ borderTop: '1px solid var(--border)' }}>
              <td style={{ padding: '8px 8px 8px 0' }}>
                {r.label}
                {r.note && <span style={{ fontSize: 'var(--text-xs)', color: MUTED }}> · {r.note}</span>}
              </td>
              <td style={{ padding: '8px', textAlign: 'right' }}><Pct cell={r.latest} strong /></td>
              <td style={{ padding: '8px', textAlign: 'right' }}><Pct cell={r.latest} field="primary_when_present" /></td>
              <td style={{ padding: '8px 0 8px 8px' }}><ReadBars cells={r.series} labels={readLabels} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const exportButtonStyle: CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: '6px',
  padding: '6px 12px', borderRadius: '8px', border: '1px solid var(--border)',
  background: 'var(--background)', color: 'var(--foreground)',
  fontSize: 'var(--text-xs)', fontWeight: 600, cursor: 'pointer',
};

export function BeliefPage({ customerId, beliefId, onBack, beliefNames = {} }: {
  customerId: number;
  beliefId: string;
  onBack: () => void;
  /** belief id -> display name, for naming another belief the report includes. */
  beliefNames?: Record<string, string>;
}) {
  const [view, setView] = useState<BeliefView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const [savingFlags, setSavingFlags] = useState(false);
  const [reportBelief, setReportBelief] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    getBeliefView(customerId, beliefId)
      .then(v => { if (!cancelled) setView(v); })
      .catch(e => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [customerId, beliefId, nonce]);

  const isAdmin = Boolean(view?.viewer_is_admin);
  const latestRunId = view?.latest_run_id;
  useEffect(() => {
    if (!isAdmin || latestRunId == null) return;
    let cancelled = false;
    getReportBelief(customerId, latestRunId)
      .then(r => { if (!cancelled) setReportBelief(r.belief_id); })
      .catch(() => { if (!cancelled) setReportBelief(undefined); });
    return () => { cancelled = true; };
  }, [isAdmin, customerId, latestRunId]);

  if (error) return <DSEmptyState message={error} />;
  if (!view || view.belief.id !== beliefId) {
    return <div style={{ padding: '32px 64px' }}>{[1, 2, 3, 4, 5].map(i => <DSLoadingRow key={i} />)}</div>;
  }

  const readLabels = view.reads.map(r => r.label);
  const latestLabel = readLabels[readLabels.length - 1];
  const header = view.header;
  const flags = new Set(view.admin?.question_flags ?? []);

  const toggleFlag = async (queryId: string, on: boolean) => {
    const next = new Set(flags);
    if (on) next.add(queryId); else next.delete(queryId);
    setSavingFlags(true);
    try {
      await saveBeliefQuestionFlags(customerId, beliefId, [...next]);
      setNonce(n => n + 1);
    } catch (e: any) {
      toast.error(e?.message || 'Could not save the flag');
    } finally {
      setSavingFlags(false);
    }
  };

  const toggleReport = async (on: boolean) => {
    try {
      const r = await saveReportBelief(customerId, view.latest_run_id, on ? beliefId : null);
      setReportBelief(r.belief_id);
      toast.success(on ? `${latestLabel} report will include this belief` : `Removed from the ${latestLabel} report`);
    } catch (e: any) {
      toast.error(e?.message || 'Could not update the report');
    }
  };

  const download = (format: 'md' | 'json') =>
    downloadBeliefExport(customerId, beliefId, format).catch((e: any) => toast.error(e?.message || 'Download failed'));

  const otherReportBelief = reportBelief && reportBelief !== beliefId
    ? (beliefNames[reportBelief] ?? reportBelief)
    : null;

  return (
    <div style={{ padding: '28px 64px 40px' }}>
      <button onClick={onBack} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: MUTED, fontSize: 'var(--text-sm)', marginBottom: '18px' }}>
        <ArrowLeft size={14} /> Back to Deep Signal
      </button>

      {/* Header */}
      <SectionLabel>Belief</SectionLabel>
      <SectionTitle size="xl">{view.belief.name}</SectionTitle>
      {view.belief.summary && (
        <p style={{ fontSize: 'var(--text-sm)', color: MUTED, lineHeight: 1.55, maxWidth: '820px', marginBottom: '6px' }}>{view.belief.summary}</p>
      )}
      {view.belief.rubric.score_2 && (
        <p style={{ fontSize: 'var(--text-xs)', color: MUTED, lineHeight: 1.55, maxWidth: '820px' }}>
          Scores 2 when: {view.belief.rubric.score_2}
        </p>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px', margin: '20px 0 12px' }}>
        <Tile label={`Present in ${latestLabel} answers`} cell={header.latest} field="share" series={header.series} readLabels={readLabels} />
        <Tile label={`Primary rec when present`} cell={header.latest} field="primary_when_present" series={header.series} readLabels={readLabels} />
        <Tile label={`Primary rec when absent`} cell={header.latest} field="primary_when_absent" series={header.series} readLabels={readLabels} />
      </div>
      <p style={{ fontSize: 'var(--text-sm)', color: 'var(--foreground)', marginBottom: '16px' }}>
        Surfaced on <b>{header.questions_surfaced}</b> of {header.questions_in_battery} questions in {latestLabel}:{' '}
        {header.questions_surfaced_asking} that ask for it, {header.questions_surfaced_unprompted} that do not.{' '}
        {header.questions_never_surfaced} have never surfaced it in the series.
      </p>

      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '28px' }}>
        <button style={exportButtonStyle} onClick={() => download('md')}><Download size={13} /> Markdown</button>
        <button style={exportButtonStyle} onClick={() => download('json')}><Download size={13} /> JSON</button>
        {isAdmin && reportBelief !== undefined && (
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: 'var(--text-xs)', color: 'var(--foreground)', marginLeft: '6px' }}
            title={otherReportBelief ? `The ${latestLabel} report currently includes "${otherReportBelief}"; ticking this replaces it.` : undefined}>
            <input type="checkbox" checked={reportBelief === beliefId} onChange={e => toggleReport(e.target.checked)} />
            Include in the {latestLabel} report
            {otherReportBelief && <span style={{ color: MUTED }}>(replaces {otherReportBelief})</span>}
          </label>
        )}
      </div>

      {/* Where it surfaces */}
      <SectionLabel>Where it surfaces</SectionLabel>
      <p style={{ fontSize: 'var(--text-xs)', color: MUTED, marginBottom: '10px' }}>
        Share of answers where the judge found the belief, per question. Bars run {readLabels[0]} to {latestLabel}.
        {isAdmin && ' Hover a figure for its counts.'}
      </p>
      {view.bands.filter(b => b.questions.length > 0).map(band => (
        <div key={band.band} style={{ marginBottom: '26px' }}>
          <p style={{ fontSize: 'var(--text-base)', fontWeight: 600, color: 'var(--foreground)', margin: '14px 0 4px' }}>
            {band.title} <span style={{ color: MUTED, fontWeight: 400 }}>({band.questions.length})</span>
          </p>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ color: MUTED, fontSize: 'var(--text-xs)', textAlign: 'left' }}>
                  <th style={{ padding: '6px 8px 6px 0', fontWeight: 600 }}>Question</th>
                  <th style={{ padding: '6px 8px', fontWeight: 600 }}>Asks for it</th>
                  <th style={{ padding: '6px 8px', fontWeight: 600 }}>By read</th>
                  <th style={{ padding: '6px 8px', fontWeight: 600, textAlign: 'right' }}>{latestLabel}</th>
                  <th style={{ padding: '6px 8px', fontWeight: 600, textAlign: 'right' }}>Primary when present</th>
                  <th style={{ padding: '6px 0 6px 8px', fontWeight: 600 }}>Judge evidence</th>
                </tr>
              </thead>
              <tbody>
                {band.questions.map(q => (
                  <QuestionRow
                    key={`${q.query_id}:${q.marker?.version ?? 0}`}
                    q={q}
                    readLabels={readLabels}
                    isAdmin={isAdmin}
                    flagged={flags.has(q.query_id)}
                    onToggleFlag={toggleFlag}
                    saving={savingFlags}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {/* By lane and by role */}
      <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap', marginTop: '12px' }}>
        <SmallTable
          title={`By lane (${latestLabel})`}
          column="Lane"
          readLabels={readLabels}
          rows={view.by_lane.map(r => ({ key: r.lane, label: r.label, note: r.in_blend ? undefined : 'not in blend', latest: r.latest, series: r.series }))}
        />
        <SmallTable
          title={`By persona (${latestLabel})`}
          column="Persona"
          readLabels={readLabels}
          rows={view.by_persona.map(r => ({ key: r.persona, label: r.persona, latest: r.latest, series: r.series }))}
        />
      </div>

      <p style={{ fontSize: 'var(--text-xs)', color: MUTED, marginTop: '24px', lineHeight: 1.6 }}>
        Present means the judge scored the belief above 0 on that answer. Errored and excluded calls are never counted.
        {' '}* Directional: fewer than {view.rules.trend_min_answers} answers behind the figure.
        {view.reads.some(r => !r.belief_measured) && ' n/m: the belief was not in the rubric for that read.'}
        {view.rebaselines.length > 0 && ` Reads before a series rebaseline use the questions and rubric in force at the time.`}
      </p>
    </div>
  );
}
